import classNames from "classnames";
import Divider from "@/components/Divider";

export interface MembershipOfficer {
  name: string;
  title: string;
}

export interface MembershipCardTextLine {
  text: string;
  tone?: "primary" | "secondary";
  emphasis?: "regular" | "strong";
}

export interface MembershipCardLayoutData {
  header: {
    overline: string;
  };
  topRow: {
    media?: {
      src?: string;
      alt?: string;
      fallback?: "empty" | "initials";
      placeholderText?: string;
    };
    lines: MembershipCardTextLine[];
  };
  section: {
    text: string;
  };
  logo: {
    src: string;
    alt: string;
    caption?: string;
  };
  rightPanel: {
    header: string;
    year: string;
    officers: MembershipOfficer[];
  };
}

export interface MembershipCardData {
  orgName: string;
  memberName: string;
  membershipType: string;
  membershipSubtitle?: string;
  electedLabel?: string;
  electedDate: string;
  memberId: string;
  sectionName: string;
  validityLabel: string;
  validityYear: string;
  officers: MembershipOfficer[];
  logoSrc: string;
  logoAlt: string;
  memberBadgeLabel?: string;
}

export interface MembershipCardProps {
  data: MembershipCardLayoutData;
  className?: string;
  size?: "sm" | "md" | "lg";
  "aria-label"?: string;
  "aria-hidden"?: boolean;
}

const sizeStyles = {
  sm: {
    leftPadding: "py-3 pr-3 pl-2",
    rightPadding: "py-3 pr-3 pl-2.5",
    topSpacing: "mt-2.5",
    topGap: "gap-2.5",
    sectionSpacing: "mt-3",
    logoSpacing: "mt-1.5",
    photoSize: "h-[80px] w-[80px]",
    yearClass: "text-[clamp(21px,6.6cqw,27px)]",
    rightContentOffset: "mt-[clamp(10px,3.2cqw,16px)]",
    leftNameClass: "text-[clamp(10px,2.5cqw,13px)]",
    leftLineClass: "text-[clamp(9px,2.2cqw,11px)]",
    sectionClass: "text-[clamp(10px,2.6cqw,14px)]"
  },
  md: {
    leftPadding: "py-4 pr-4 pl-2.5",
    rightPadding: "py-4 pr-4 pl-3",
    topSpacing: "mt-3",
    topGap: "gap-3",
    sectionSpacing: "mt-3",
    logoSpacing: "mt-[clamp(4px,1.2cqw,8px)]",
    photoSize: "h-[88px] w-[88px]",
    yearClass: "text-[clamp(24px,7.1cqw,30px)]",
    rightContentOffset: "mt-[clamp(12px,3.6cqw,20px)]",
    leftNameClass: "text-[clamp(11px,2.9cqw,15px)]",
    leftLineClass: "text-[clamp(9px,2.3cqw,12px)]",
    sectionClass: "text-[clamp(12px,3.1cqw,18px)]"
  },
  lg: {
    leftPadding: "py-5 pr-5 pl-3",
    rightPadding: "py-5 pr-5 pl-4",
    topSpacing: "mt-3.5",
    topGap: "gap-3.5",
    sectionSpacing: "mt-4",
    logoSpacing: "mt-[clamp(6px,2.1cqw,12px)]",
    photoSize: "h-[104px] w-[104px]",
    yearClass: "text-[clamp(27px,7.9cqw,34px)]",
    rightContentOffset: "mt-[clamp(14px,4cqw,24px)]",
    leftNameClass: "text-[clamp(12px,3.1cqw,16px)]",
    leftLineClass: "text-[clamp(10px,2.6cqw,13px)]",
    sectionClass: "text-[clamp(13px,3.4cqw,20px)]"
  }
} as const;

function getInitials(fullName: string): string {
  const initials = fullName
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("");
  return initials || "PGA";
}

function lineClass(line: MembershipCardTextLine, size: MembershipCardProps["size"]): string {
  void size;
  const toneClass = line.tone === "secondary" ? "text-sys-brand-on-surface-variant" : "text-sys-brand-on-surface";
  const emphasisClass = line.emphasis === "strong" ? "font-semibold" : "font-normal";
  return classNames("leading-tight", toneClass, emphasisClass);
}

function lineIndexClass(index: number): string {
  switch (index) {
    case 0:
      return "text-[clamp(12px,3.1cqw,16px)] leading-tight font-semibold";
    case 1:
      return "text-[clamp(11px,2.9cqw,15px)] leading-tight font-semibold";
    case 2:
      return "text-[clamp(10px,2.35cqw,13px)] leading-tight";
    default:
      return "text-[clamp(10px,2.5cqw,13px)] leading-tight font-semibold";
  }
}

export function mapMembershipCardDataToLayout(
  data: MembershipCardData,
  photoSrc?: string,
  photoAlt?: string
): MembershipCardLayoutData {
  const lines: MembershipCardTextLine[] = [
    { text: data.memberName, tone: "primary", emphasis: "strong" },
    { text: data.membershipType, tone: "primary", emphasis: "strong" }
  ];

  if (data.membershipSubtitle) {
    lines.push({ text: data.membershipSubtitle, tone: "secondary", emphasis: "regular" });
  }

  lines.push(
    { text: `${data.electedLabel ?? "Elected"} ${data.electedDate}`, tone: "primary", emphasis: "strong" },
    { text: data.memberId, tone: "primary", emphasis: "strong" }
  );

  return {
    header: {
      overline: data.orgName
    },
    topRow: {
      media: {
        src: photoSrc,
        alt: photoAlt ?? `${data.memberName} photo`,
        fallback: photoSrc ? "initials" : "empty",
        placeholderText: "Submit Photo on PGA.org"
      },
      lines
    },
    section: {
      text: data.sectionName
    },
    logo: {
      src: data.logoSrc,
      alt: data.logoAlt,
      caption: data.memberBadgeLabel
    },
    rightPanel: {
      header: data.validityLabel,
      year: data.validityYear,
      officers: data.officers
    }
  };
}

export default function MembershipCard({
  data,
  className,
  size = "md",
  "aria-label": ariaLabel = "PGA Membership Card",
  "aria-hidden": ariaHidden
}: MembershipCardProps) {
  const styles = sizeStyles[size];
  const firstLineText = data.topRow.lines[0]?.text ?? "";

  return (
    <article
      aria-label={ariaLabel}
      aria-hidden={ariaHidden}
      className={classNames(
        "grid aspect-[1.586/1] w-full min-w-0 overflow-hidden rounded-[20px] border border-sys-brand-outline-variant bg-white [container-type:inline-size]",
        "grid-cols-[70fr_30fr]",
        "shadow-web-1",
        className
      )}
      data-testid="membership-card"
    >
      <section
        className={classNames(
          "flex min-w-0 flex-col justify-between border-r border-sys-brand-outline-variant bg-white",
          styles.leftPadding
        )}
      >
        <div className="min-h-0">
          <p className="whitespace-nowrap text-[clamp(9px,2.15cqw,13px)] leading-none font-semibold uppercase tracking-[0.06em] text-slate-700">
            {data.header.overline}
          </p>

          <div
            className={classNames("grid min-w-0 grid-cols-[auto_1fr] items-start", styles.topSpacing, styles.topGap)}
          >
            <div
              className={classNames(
                "flex items-center justify-center overflow-hidden bg-sys-brand-surface-container text-sys-brand-on-surface-variant",
                styles.photoSize
              )}
            >
              {data.topRow.media?.src ? (
                <img
                  src={data.topRow.media.src}
                  alt={data.topRow.media.alt ?? "Member photo"}
                  className="h-full w-full object-cover"
                />
              ) : (
                <div className="flex h-full w-full items-center justify-center px-2">
                  {data.topRow.media?.fallback === "initials" ? (
                    <span className="text-title-large font-semibold">{getInitials(firstLineText)}</span>
                  ) : data.topRow.media?.placeholderText ? (
                    <p className="text-center text-[clamp(8px,2cqw,12px)] leading-tight text-sys-brand-on-surface-variant">
                      {data.topRow.media.placeholderText}
                    </p>
                  ) : null}
                </div>
              )}
            </div>

            <div className="flex min-w-0 flex-col gap-[clamp(2px,0.8cqw,6px)] text-left">
              {data.topRow.lines.map((line, index) => (
                <p key={`${line.text}-${index}`} className={classNames(lineClass(line, size), lineIndexClass(index))}>
                  {line.text}
                </p>
              ))}
            </div>
          </div>

          <p
            className={classNames(
              "whitespace-nowrap leading-tight font-semibold text-sys-brand-on-surface",
              styles.sectionSpacing,
              styles.sectionClass
            )}
          >
            {data.section.text}
          </p>
        </div>

        <div className={classNames("shrink-0", styles.logoSpacing)}>
          <div className="h-[clamp(55px,12.8cqw,72px)]">
            <img src={data.logo.src} alt={data.logo.alt} className="h-full w-auto max-w-full object-contain" />
          </div>
        </div>
      </section>

      <section
        className={classNames(
          "flex flex-col items-end bg-[linear-gradient(180deg,#163079_0%,#0D1D57_70%,#0B1848_100%)] text-sys-brand-on-primary",
          styles.rightPadding
        )}
      >
        <div className={classNames("w-full", styles.rightContentOffset)}>
          <p className="text-right whitespace-pre-line text-[clamp(8.5px,1.95cqw,12px)] leading-tight text-white/90">
            {data.rightPanel.header}
          </p>
          <p
            className={classNames(
              "mt-[clamp(4px,1.2cqw,8px)] text-right leading-none font-semibold text-white",
              styles.yearClass
            )}
          >
            {data.rightPanel.year}
          </p>
          <Divider className="my-[clamp(6px,1.8cqw,12px)] w-full [--md-divider-color:rgba(255,255,255,0.32)]" />

          <ul className="mt-[clamp(2px,0.6cqw,4px)] w-full space-y-[clamp(3px,0.85cqw,7px)] text-right">
            {data.rightPanel.officers.map((officer) => (
              <li key={`${officer.name}-${officer.title}`} className="min-w-0">
                <p className="text-[clamp(9.5px,2.1cqw,12px)] leading-tight font-semibold text-white">{officer.name}</p>
                <p className="text-[clamp(8.5px,1.8cqw,10.5px)] leading-tight text-white/85">{officer.title}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>
    </article>
  );
}
